import { useQuery } from '@tanstack/react-query';
import {
  useQAPairAICanNotReply,
  useQAPairAIReplyError,
  useQAPairManagerPermission,
  useSetQAPairConversationKnowledgeQuery,
} from './im/useQAPair';
import { queries } from 'services/queries';
import { useEffect, useMemo, useRef } from 'react';
import { eventBus } from 'services/eventBus';
import { isIdEqual } from 'utils/is';
import { debounce } from 'lodash-es';

export function useConversationKnowledgeQueryLoader(conversationId: string, isGalaxy?: boolean) {
  const managerPermission = useQAPairManagerPermission();
  const aiCanNotReplyPermission = useQAPairAICanNotReply();
  const aiReplyErrorPermission = useQAPairAIReplyError();

  const setQAPairConversationKnowledgeQuery = useSetQAPairConversationKnowledgeQuery();

  const enabled =
    (aiCanNotReplyPermission || aiReplyErrorPermission || managerPermission) &&
    !!conversationId &&
    !isGalaxy;

  const { data: knowledgeQuery, refetch: refetchKnowledgeQuery } = useQuery({
    ...queries.conversations.knowledgeQuery(conversationId),
    enabled,
  });

  // 创建防抖版本的 refetchKnowledgeQuery，防止高频触发
  const debouncedRefetchRef = useRef<ReturnType<typeof debounce>>();

  const debouncedRefetchKnowledgeQuery = useMemo(() => {
    // 清理之前的防抖函数
    if (debouncedRefetchRef.current) {
      debouncedRefetchRef.current.cancel();
    }

    // 创建新的防抖函数
    const debouncedFn = debounce(() => {
      refetchKnowledgeQuery();
    }, 2000);

    debouncedRefetchRef.current = debouncedFn;
    return debouncedFn;
  }, [refetchKnowledgeQuery]);

  // 组件卸载时清理防抖函数
  useEffect(() => {
    return () => {
      if (debouncedRefetchRef.current) {
        debouncedRefetchRef.current.cancel();
      }
    };
  }, []);

  useEffect(() => {
    if (knowledgeQuery) {
      setQAPairConversationKnowledgeQuery(knowledgeQuery);
    }
  }, [knowledgeQuery, setQAPairConversationKnowledgeQuery]);

  useEffect(() => {
    if (!enabled) {
      return;
    }

    const handler = (id: string) => {
      if (isIdEqual(id, conversationId)) {
        debouncedRefetchKnowledgeQuery();
      }
    };
    eventBus.on('QAPAIR_CONV_KNOWLEDGE_QUERY_REFETCH', handler);
    return () => {
      eventBus.off('QAPAIR_CONV_KNOWLEDGE_QUERY_REFETCH', handler);
    };
  }, [conversationId, debouncedRefetchKnowledgeQuery, enabled]);
}
